import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, AlertTriangle, Calendar, User, Package, ChevronRight } from "lucide-react";
import { formatDataItaliana } from "@/utils/dateUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerificaCompletamentiPage({ user, logout }) {
  const [commesse, setCommesse] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchCommesseDaVerificare = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/commesse-pose/verifica-completamenti`, { withCredentials: true });
      setCommesse(res.data);
    } catch (e) {
      console.error("Error fetching commesse da verificare:", e);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommesseDaVerificare();
  }, []);

  // Filtra per ricerca
  const commesseFiltrate = commesse.filter(c => {
    const searchLower = searchTerm.toLowerCase();
    return (
      c.cliente_nome.toLowerCase().includes(searchLower) ||
      c.cliente_cognome.toLowerCase().includes(searchLower)
    );
  });

  return (
    <PageLayout user={user} logout={logout} testId="verifica-completamenti-page" className="overflow-y-auto">
      <div className="p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-6 h-6 text-orange-500" />
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Verifica Completamenti</h1>
            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
              {commesse.length}
            </span>
          </div>
          <p className="text-slate-600 text-sm">
            Commesse con data posa passata e articoli non ancora completati
          </p>
        </div>

        {/* Barra di ricerca */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                data-testid="search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contenuto */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : commesseFiltrate.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-green-500 mb-4">
                <Package className="w-12 h-12 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {searchTerm ? "Nessun risultato trovato" : "Tutto in ordine!"}
              </h3>
              <p className="text-slate-600">
                {searchTerm 
                  ? "Prova a modificare i criteri di ricerca" 
                  : "Non ci sono commesse con articoli da verificare"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {commesseFiltrate.map((commessa) => (
              <Card 
                key={commessa.id} 
                className="hover:shadow-md transition-shadow border-l-4 border-l-orange-500"
              >
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Info Cliente */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-slate-500" />
                        <span className="font-semibold text-slate-900">
                          {commessa.cliente_nome} {commessa.cliente_cognome}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Data Posa: <strong>{formatDataItaliana(commessa.data_posa)}</strong></span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Package className="w-4 h-4" />
                          <span>
                            Articoli da completare: 
                            <strong className="text-orange-600 ml-1">
                              {commessa.articoli_mancanti}/{commessa.totale_articoli}
                            </strong>
                          </span>
                        </div>
                      </div>

                      {/* Dettaglio articoli mancanti */}
                      {commessa.articoli_mancanti_nomi && commessa.articoli_mancanti_nomi.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {commessa.articoli_mancanti_nomi.slice(0, 3).map((nome, idx) => (
                            <span 
                              key={idx} 
                              className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs"
                            >
                              {nome}
                            </span>
                          ))}
                          {commessa.articoli_mancanti_nomi.length > 3 && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                              +{commessa.articoli_mancanti_nomi.length - 3} altri
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Giorni passati e pulsante */}
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          {commessa.giorni_passati}
                        </div>
                        <div className="text-xs text-slate-500">
                          {commessa.giorni_passati === 1 ? "giorno fa" : "giorni fa"}
                        </div>
                      </div>
                      
                      <Link to={`/commesse-pose/${commessa.id}?tab=programmazione`}>
                        <Button variant="outline" className="whitespace-nowrap">
                          Dettaglio
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
